import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
// ---------------------------------------------------------------------------
// Wave 2.29 BE-2 (TASK-083) — system analytics read endpoints.
// Super-admin-only dashboard: MRR + signups + churn + queue depth
// (admin-arch §3.10 + admin-prd §9.2).
//
// Monetary values stay in kopecks (bigint on the wire) per ADR-010 —
// formatting to roubles happens at the FE boundary.
// ---------------------------------------------------------------------------
/** `YYYY-MM-DD` calendar date. */
const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
/**
 * Bucket period for signups + churn endpoints. Closed enum so the FE chart
 * axis labels match the BE bucket boundaries 1-to-1.
 */
export const systemAnalyticsPeriodSchema = z.enum([
    'daily',
    'weekly',
    'monthly',
]);
/**
 * Query for `GET /admin/system-analytics/signups`.
 *
 * Both `from` and `to` are optional. Defaults are bucket-aware (last 30 days
 * for daily, 12 weeks for weekly, 12 months for monthly). The runtime
 * `to > from` ordering check and the 365-day max-window clamp live in the
 * service so the error carries a human message.
 */
export const systemAnalyticsSignupsQuerySchema = z
    .object({
    period: systemAnalyticsPeriodSchema.default('daily'),
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
})
    .strict();
/**
 * Query for `GET /admin/system-analytics/churn`. Same shape as signups;
 * only the `period='monthly'` default differs because churn rate is only
 * meaningful at monthly granularity (cohort size at start-of-bucket).
 */
export const systemAnalyticsChurnQuerySchema = z
    .object({
    period: systemAnalyticsPeriodSchema.default('monthly'),
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// MRR response — current month + previous-month delta + per-tariff split.
// ---------------------------------------------------------------------------
/**
 * Per-tariff slice inside the MRR response. `mrr_kop` is the sum of
 * `subscriptions.monthly_amount_kop` across rows of this tariff that are
 * active at `now()`.
 */
export const systemAnalyticsMrrByTariffSchema = z.object({
    tariff_id: uuidSchema,
    tariff_name: z.string(),
    count_subscriptions: z.number().int().nonnegative(),
    mrr_kop: z.number().int().nonnegative(),
});
/**
 * Wire DTO for `GET /admin/system-analytics/mrr` (admin-arch §3.10, ADR-048).
 *
 * `previous_month_mrr_kop` is the MRR stored in the paying snapshot of the day
 * one month before today (BUG-404). `null` when that day has no snapshot: before
 * the history starts, or a missed day. The past is never interpolated, so the FE
 * shows «—». A snapshot where nobody paid is a real `0`.
 *
 * `history_since` is the first day with a snapshot, read as «история с
 * ДД.ММ.ГГГГ»; `null` when nothing has been captured yet.
 *
 * `delta_percent` is `(current - previous) / previous * 100`, rounded to
 * 2 decimals server-side. When `previous_month_mrr_kop` is `null` or `0` the
 * field is omitted (no baseline → no percentage) instead of returning
 * `Infinity`.
 */
export const systemAnalyticsMrrResponseSchema = z.object({
    current_mrr_kop: z.number().int().nonnegative(),
    previous_month_mrr_kop: z.number().int().nonnegative().nullable(),
    history_since: isoDateSchema.nullable(),
    delta_percent: z.number().optional(),
    by_tariff: z.array(systemAnalyticsMrrByTariffSchema),
});
// ---------------------------------------------------------------------------
// Signups bucket response.
// ---------------------------------------------------------------------------
export const systemAnalyticsSignupsBucketSchema = z.object({
    bucket_start: isoDateTimeSchema,
    signups: z.number().int().nonnegative(),
});
export const systemAnalyticsSignupsResponseSchema = z.object({
    period: systemAnalyticsPeriodSchema,
    window: z.object({
        from: isoDateTimeSchema,
        to: isoDateTimeSchema,
    }),
    buckets: z.array(systemAnalyticsSignupsBucketSchema),
});
// ---------------------------------------------------------------------------
// Churn bucket response.
// ---------------------------------------------------------------------------
/**
 * `churn_rate_percent` is `churned_accounts / active_at_bucket_start * 100`,
 * rounded to 2 decimals. Omitted when `active_at_bucket_start = 0` (no
 * cohort → no rate) so the FE can render "—" rather than NaN.
 */
export const systemAnalyticsChurnBucketSchema = z.object({
    bucket_start: isoDateTimeSchema,
    churned_accounts: z.number().int().nonnegative(),
    churn_rate_percent: z.number().nonnegative().optional(),
    new_accounts: z.number().int().nonnegative(),
    net_change: z.number().int(),
});
export const systemAnalyticsChurnResponseSchema = z.object({
    period: systemAnalyticsPeriodSchema,
    window: z.object({
        from: isoDateTimeSchema,
        to: isoDateTimeSchema,
    }),
    buckets: z.array(systemAnalyticsChurnBucketSchema),
});
// ---------------------------------------------------------------------------
// Queue depth response — bulk jobs + notification queue.
// ---------------------------------------------------------------------------
/**
 * Per-queue depth row. `queue_name` is opaque on the wire — the BE picks a
 * stable label per source (`encode`, `notifications`, `bulk_jobs`, etc.).
 *
 * `description` is a short Russian human-readable explanation of what the
 * queue does — surfaced in the admin UI next to the counters so the
 * operator does not have to grep the worker code to remember which queue
 * is responsible for what. Wave 2.68 BE addition.
 *
 * `oldest_pending_age_seconds` reports the age of the oldest row still in
 * `pending` state; `null` when the queue is empty.
 *
 * Counters are zero (and `oldest_pending_age_seconds` is `null`) for known
 * queues that have no rows in the DB — the BE returns ALL known queues so
 * the UI shows the full worker stack even when idle, not just the queues
 * that happen to have pending work.
 */
export const systemAnalyticsQueueRowSchema = z.object({
    queue_name: z.string(),
    description: z.string(),
    pending: z.number().int().nonnegative(),
    processing: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    oldest_pending_age_seconds: z.number().int().nonnegative().nullable(),
});
export const systemAnalyticsQueueDepthResponseSchema = z.object({
    by_queue: z.array(systemAnalyticsQueueRowSchema),
    total_pending: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Wave 2.65 BE (TASK-084-followup-system-heatmap) — system-wide 7×24 playback
// heatmap. Aggregates `content.play_events.started_at` into 168 cells across
// every account (admin-arch §3.10 / admin-prd §9.2 "система" dashboard).
//
// ISO weekday convention is used so the FE chart axis labels Mon..Sun without
// a relabel pass: `day_of_week = EXTRACT(ISODOW FROM started_at) - 1` ⇒
// 0=Mon … 6=Sun.
// ---------------------------------------------------------------------------
/** `from` / `to` are window boundaries; the service defaults to last-30-days. */
export const systemHeatmapQuerySchema = z
    .object({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
})
    .strict();
export const systemHeatmapCellSchema = z
    .object({
    day_of_week: z.number().int().min(0).max(6),
    hour: z.number().int().min(0).max(23),
    play_count: z.number().int().nonnegative(),
})
    .strict();
export const systemHeatmapResponseSchema = z
    .object({
    from: isoDateSchema,
    to: isoDateSchema,
    cells: z.array(systemHeatmapCellSchema),
})
    .strict();
// ---------------------------------------------------------------------------
// BUG-278 — "проблемные треки": the catalog-quality view of dislikes
// (admin-prd.en §Q19 "top 50 tracks by dislikes (problem tracks)").
//
// GET /admin/system-analytics/problem-tracks?limit=
//
// Ranks published tracks by how many venues currently block them
// (`customers.track_dislikes` with `removed_at IS NULL`). Since BUG-277 a
// dislike is a standing rotation block, so a track high in this list is
// literally unplayable in that many venues — the signal an art director needs
// to decide whether to deprecate it.
// ---------------------------------------------------------------------------
export const problemTracksQuerySchema = z
    .object({
    limit: z.coerce.number().int().min(1).max(50).default(50),
})
    .strict();
/**
 * `reasons` maps the stored reason code to its count for this track, e.g.
 * `{ inappropriate: 4, other: 1 }`. Rows with a NULL reason are counted under
 * the `unspecified` key. Free-form keys (the column has no CHECK) pass through
 * so an unknown code is visible instead of silently dropped.
 */
export const problemTrackRowSchema = z
    .object({
    track_id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    dislikes: z.number().int().positive(),
    venues: z.number().int().positive(),
    reasons: z.record(z.string(), z.number().int().positive()),
})
    .strict();
export const problemTracksResponseSchema = z
    .object({
    limit: z.number().int().positive(),
    rows: z.array(problemTrackRowSchema),
})
    .strict();
// ---------------------------------------------------------------------------
// Founders' analytics (founders-analytics arch §5).
// ---------------------------------------------------------------------------
/**
 * Query of every windowed read of founders' analytics; the admin sends
 * `rangeToWindow(range)`. The instants are `[from, to)`.
 *
 * `to` must come after `from` and at most 365 days later, otherwise 400. The API
 * applies that rule (`resolveAnalyticsWindow`), as it does for signups and churn:
 * the error then carries `INVALID_WINDOW` or `WINDOW_TOO_LARGE`, and this stays a
 * plain object that a read with more parameters can `.extend()`.
 */
export const systemAnalyticsWindowQuerySchema = z
    .object({
    from: isoDateTimeSchema,
    to: isoDateTimeSchema,
})
    .strict();
/**
 * One person of «Загрузки»: a staff member of any role. `tracks` counts their
 * tracks with `created_at` in `[from, to)` not deleted, archived or rejected now, so it is
 * never 0: staff without such a track have no row.
 */
export const systemAnalyticsUploadsRowSchema = z
    .object({
    user_id: uuidSchema,
    name: z.string(),
    tracks: z.number().int().positive(),
})
    .strict();
/** `GET /admin/system-analytics/uploads`. Rows: most tracks first, then by name. */
export const systemAnalyticsUploadsResponseSchema = z
    .object({
    window: z
        .object({
        from: isoDateTimeSchema,
        to: isoDateTimeSchema,
    })
        .strict(),
    rows: z.array(systemAnalyticsUploadsRowSchema),
})
    .strict();
//# sourceMappingURL=system-analytics.js.map